import { SPARK_RESOURCE_TYPES } from 'constants/common';
import { NULL_CACHE_AND_STALE_TIME } from 'constants/index';
import React from 'react';
import toastr from 'react-hot-toast';
import { useQuery } from 'react-query';
import type {
  JoinedPluginAuthenticationParameter,
  SinglePluginAuthenticationParameterResponse,
} from 'types/PluginAuthenticationParameter';

import {
  generateFilter,
  generateIncludeQuery,
  generateURL,
} from 'utilities/hooks/urlGeneration';
import { numericSort } from 'utilities/sort';
import { fetchWrapper } from 'utils/fetchWrapper';

const getPluginAuthenticationParameters = (
  pluginAuthenticationID: string | null | undefined
) => {
  if (!pluginAuthenticationID) {
    return Promise.reject('pluginAuthentication ID is required.');
  }

  const queryArray = [
    generateFilter('pluginAuthenticationID', pluginAuthenticationID),
    generateIncludeQuery(['parameter']),
  ];

  const url = generateURL(
    SPARK_RESOURCE_TYPES.PLUGIN_AUTHENTICATION_PARAMETERS,
    queryArray
  );

  return fetchWrapper<SinglePluginAuthenticationParameterResponse>({
    url: url,
    method: 'GET',
  });
};

export const usePluginAuthenticationParameters = (
  pluginAuthenticationID: string | null | undefined
) => {
  const query = useQuery(
    ['pluginAuthenticationParameters', pluginAuthenticationID],
    () => getPluginAuthenticationParameters(pluginAuthenticationID),
    {
      onError: (error) => {
        if (error instanceof Error) {
          toastr.error(
            `failed to Fetch Plugin Authentication Parameter: ${error.message}`
          );
        }
      },
      enabled: Boolean(pluginAuthenticationID),
      ...NULL_CACHE_AND_STALE_TIME,
    }
  );

  const { data } = query;
  const { data: pluginAuthenticationParameters, included: parameters } =
    data || {};

  const joinedPluginAuthenticationPluginParameters:
    | JoinedPluginAuthenticationParameter[]
    | undefined = React.useMemo(() => {
    return pluginAuthenticationParameters?.map((authenticationParameter) => {
      const parameter = parameters?.find(
        (parameter) =>
          parameter.id === authenticationParameter.attributes.parameterID
      );
      return {
        pluginAuthenticationID:
          authenticationParameter.attributes.pluginAuthenticationID,
        pluginAuthenticationParameterID: authenticationParameter.id,
        id: parameter?.id,
        sortOrder: authenticationParameter.attributes.sortOrder,
        parameterAttributes: parameter?.attributes,
      } as JoinedPluginAuthenticationParameter;
    });
  }, [parameters, pluginAuthenticationParameters]);

  return {
    pluginAuthenticationParameters,
    joinedPluginAuthenticationPluginParameters:
      joinedPluginAuthenticationPluginParameters?.sort((a, b) =>
        numericSort(a.sortOrder, b.sortOrder)
      ),
    ...query,
  };
};
